import type { ReactNode } from 'react';
import { StatusBadge } from '../ui';
import type { Level } from '../EnvironmentReadiness';

/**
 * The layout contract of the project area, as components rather than
 * conventions: one grid, one card, one section header. Every project screen
 * is built from these, so the left and right edges of every section line up
 * and no card invents its own width or padding.
 */

/** ProjectSection stacks major sections with the one section rhythm. */
export function ProjectSection({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <div className="project-section" aria-label={label}>
      {children}
    </div>
  );
}

type Span = 3 | 4 | 6 | 8 | 12;

/** ProjectGrid is the 12-column grid every section lays out on. */
/** ProjectGrid lays out the section's columns. `equal`: the cards of one
 * row share the same height, for a pair of cards read side by side. */
export function ProjectGrid({ children, equal = false }: { children: ReactNode; equal?: boolean }) {
  return <div className={`project-grid${equal ? ' project-grid--equal' : ''}`}>{children}</div>;
}

export function ProjectCol({ span, children }: { span: Span; children: ReactNode }) {
  return <div className={`project-grid__col project-grid__col--${span}`}>{children}</div>;
}

/** SectionHeader and SummaryCard are the shared primitives (CO-88); the
 * names stay for the project area's existing call sites. */
export { SectionHeader, SummaryTile as SummaryCard } from '../primitives';

/**
 * PanelCard is the project area's only card: one border, one padding, one
 * radius. Nesting one inside another is deliberately not supported — inner
 * structure is made with spacing and separators instead.
 */
export function PanelCard({
  children,
  className,
  labelledBy,
  testId,
  tone,
}: {
  children: ReactNode;
  className?: string;
  labelledBy?: string;
  testId?: string;
  tone?: Level;
}) {
  return (
    <section
      className={`panel${tone ? ` panel--${tone}` : ''}${className ? ` ${className}` : ''}`}
      aria-labelledby={labelledBy}
      data-testid={testId}
    >
      {children}
    </section>
  );
}

/**
 * LevelBadge states a readiness level in words as well as colour. It is
 * StatusBadge for kind "level": tone and default words come from labels.ts;
 * children replace the words when the context has a better sentence.
 */
export function LevelBadge({ level, children }: { level: Level; children?: ReactNode }) {
  return <StatusBadge kind="level" value={level} label={children} />;
}

/**
 * FactList renders the header's and the infrastructure groups' key/value
 * facts as a real definition list rather than middle-dot-joined text.
 */
export function FactList({ facts }: { facts: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="fact-list">
      {facts.map((fact) => (
        <div key={fact.label} className="fact-list__row">
          <dt>{fact.label}</dt>
          <dd>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * QuietEmpty is the compact counterpart of the page-level EmptyState: one
 * sentence where a whole panel would be too loud, for a section that simply
 * has nothing yet.
 */
export function QuietEmpty({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p className="quiet-empty" data-testid={testId}>
      {children}
    </p>
  );
}
